import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Heading, Text, Button, SimpleGrid, Flex, Icon, Image, Badge, Spinner, useToast,
} from '@chakra-ui/react';
import { FaUsers, FaMapMarkerAlt, FaBriefcase, FaWallet, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PageHero from '../Components/PageHero';
import { fetchJobs, formatDate, localizeRow, normalizeStorageUrl } from '../lib/content';

const contractLabelKey = (contractType) =>
  `admin.contract${String(contractType || '')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')}`;

const JobsSection = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchJobs().then(({ data, error }) => {
      if (cancelled) return;
      if (error) {
        console.error('JOBS ERROR', error);
        toast({
          title: `${t('jobs.errorPrefix')}: ${error.message}`,
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'top',
        });
      }
      setJobs(data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [toast, t]);

  const handleApply = (jobId) => {
    navigate(`/application/${jobId}`);
  };

  return (
    <Box pb={16}>
      <PageHero title={t('jobs.heroTitle')} subtitle={t('jobs.subtitle')} />
      <Box as="section" py={{ base: 14, md: 20 }} bg="surface.muted">
        <Container maxW="7xl">
          <Box maxW="700px" mx="auto" textAlign="center" mb={12}>
            <Heading as="h2" size="xl">
              {t('jobs.title')}
            </Heading>
            <Box w="56px" h="5px" bg="brand.500" borderRadius="full" mx="auto" mt={4} />
          </Box>
          {loading ? (
            <Flex justify="center" py={16}>
              <Spinner size="xl" color="brand.500" thickness="4px" />
            </Flex>
          ) : jobs.length === 0 ? (
            <Box
              bg="surface.base"
              borderRadius="2xl"
              boxShadow="sm"
              border="1px solid"
              borderColor="border.subtle"
              p={{ base: 12, md: 16 }}
              textAlign="center"
            >
              <Text color="text.soft" fontSize="lg" mb={6}>
                {t('jobs.comingSoon')}
              </Text>
              <Button
                as="a"
                href="mailto:info@microtous.com,support@microtous.com?subject=Internship%20Request"
                size="lg"
                colorScheme="brand"
              >
                {t('internship.cta')}
              </Button>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
              {jobs.map((job) => {
                const item = localizeRow(job, i18n.language);
                return (
                  <Box
                    key={job.id}
                    bg="surface.base"
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor="border.subtle"
                    boxShadow="sm"
                    overflow="hidden"
                    display="flex"
                    flexDirection="column"
                    transition="transform 0.25s ease, boxShadow 0.25s ease"
                    _hover={{ transform: 'translateY(-6px)', boxShadow: 'xl' }}
                  >
                    {job.image ? (
                      <Image
                        src={normalizeStorageUrl(job.image)}
                        alt={item.title}
                        objectFit="cover"
                        w="100%"
                        h="180px"
                      />
                    ) : (
                      <Flex
                        h="180px"
                        bgGradient="linear(to-br, brand.500, brand.700)"
                        align="center"
                        justify="center"
                      >
                        <Icon as={FaUsers} boxSize={10} color="whiteAlpha.800" />
                      </Flex>
                    )}
                    <Box p={8} display="flex" flexDirection="column" flex="1">
                      <Heading as="h3" size="md" mb={3}>
                        {item.title}
                      </Heading>
                      {item.description && (
                        <Text color="text.muted" fontSize="sm" mb={5} flex="1">
                          {item.description}
                        </Text>
                      )}
                      <Flex gap={2} flexWrap="wrap" mb={6}>
                        {job.location && (
                          <Badge colorScheme="brand" borderRadius="full" px={3} py={1} fontSize="xs">
                            <Icon as={FaMapMarkerAlt} mr={1} />
                            {job.location}
                          </Badge>
                        )}
                        {job.contract_type && (
                          <Badge colorScheme="purple" borderRadius="full" px={3} py={1} fontSize="xs">
                            <Icon as={FaBriefcase} mr={1} />
                            {t(contractLabelKey(job.contract_type))}
                          </Badge>
                        )}
                        {job.salary && (
                          <Badge colorScheme="green" borderRadius="full" px={3} py={1} fontSize="xs">
                            <Icon as={FaWallet} mr={1} />
                            {job.salary}
                          </Badge>
                        )}
                        {job.deadline && (
                          <Badge colorScheme="accent" color="brand.900" borderRadius="full" px={3} py={1} fontSize="xs">
                            <Icon as={FaCalendarAlt} mr={1} />
                            {formatDate(job.deadline, i18n.language)}
                          </Badge>
                        )}
                      </Flex>
                      <Button
                        colorScheme="brand"
                        size="sm"
                        alignSelf="flex-start"
                        onClick={() => handleApply(job.id)}
                      >
                        {t('jobs.apply')}
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </SimpleGrid>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default JobsSection;
